import React, { useState, useEffect } from 'react';
import { useUsers } from './UsersContext';
import { T, Th, Tr, ActionBtn, Modal, UserDetailFields, TableCard, TableBar, SearchInput, Toast, RejectModal, PrimaryBtn } from './theme';
import { useRefresh } from './RefreshContext';
import API_BASE_URL from '../config/api';

const ApproveEventsSection = () => {
  const { getUserName, getUserObj } = useUsers();
  const { refresh, triggerRefresh } = useRefresh();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [rejectModal, setRejectModal] = useState({ show: false, eventId: null });
  const [rejectComment, setRejectComment] = useState('');
  const [viewEvent, setViewEvent] = useState(null);
  const [viewUser, setViewUser] = useState(null);
  const [search, setSearch] = useState('');
  const reviewerName = `${localStorage.getItem('vrvName') || ''} ${localStorage.getItem('vrvSurname') || ''}`.trim() || localStorage.getItem('vrvEmail') || '';

  const fetchPendingEvents = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/vrv-events-getAll`, { method: 'GET' });
      const data = await res.json();
      const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
      setEvents((parsed.data || []).filter(e => e.status === 'PENDING').sort((a, b) => new Date(b.event_start_date || 0) - new Date(a.event_start_date || 0)));
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchPendingEvents(); }, [refresh]); // eslint-disable-line

  const showMsg = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3500);
  };

  const handleAction = async (event_id, action, comments = '') => {
    try {
      const token = localStorage.getItem('vrvToken');
      const res = await fetch(`${API_BASE_URL}/vrv-events-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ event_id, action, reviewedBy: reviewerName, reviewedAt: new Date().toISOString(), reviewComments: action === 'APPROVE' ? 'APPROVED' : comments }),
      });
      if (res.ok) { showMsg(`Event ${action.toLowerCase()}d successfully!`, 'success'); fetchPendingEvents(); triggerRefresh(); }
      else showMsg('Action failed', 'error');
    } catch { showMsg('Error connecting to server', 'error'); }
  };

  const filteredEvents = events.filter(e => search === '' || Object.values(e).some(val => String(val).toLowerCase().includes(search.toLowerCase())));

  if (loading) return <p className="text-center py-8 text-sm" style={{ color: T.muted }}>Loading...</p>;
  if (events.length === 0) return <p className="text-center py-8 text-sm" style={{ color: T.muted }}>No pending events to approve.</p>;

  return (
    <div>
      <div className="mb-4"><SearchInput value={search} onChange={e => setSearch(e.target.value)} /></div>

      {/* Desktop Table */}
      <div className="desktop-table">
        <TableCard>
          <TableBar>
            <p className="text-xs font-bold" style={{ color: T.gold }}>{filteredEvents.length} event(s) pending approval</p>
          </TableBar>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr><Th>Title</Th><Th>Created By</Th><Th>Start Date</Th><Th>End Date</Th><Th center>Actions</Th></tr></thead>
              <tbody>
                {filteredEvents.map((event, i) => (
                  <Tr key={event.event_id || i}>
                    <td className="px-3 py-2.5 font-medium" style={{ color: T.ink }}>{event.title || '—'}</td>
                    <td className="px-3 py-2.5">{event.createdBy ? <button onClick={() => { const u = getUserObj(event.createdBy); if (u) setViewUser(u); }} className="font-bold hover:underline text-xs" style={{ color: T.gold }}>{getUserName(event.createdBy) || '...'}</button> : '—'}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap" style={{ color: T.muted }}>{event.event_start_date ? new Date(event.event_start_date).toLocaleDateString() : '—'}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap" style={{ color: T.muted }}>{event.event_end_date ? new Date(event.event_end_date).toLocaleDateString() : '—'}</td>
                    <td className="px-3 py-2.5 text-center">
                      <div className="flex gap-1.5 justify-center">
                        <ActionBtn onClick={() => setViewEvent(event)} color="muted">View</ActionBtn>
                        <ActionBtn onClick={() => handleAction(event.event_id, 'APPROVE')} color="green">Approve</ActionBtn>
                        <ActionBtn onClick={() => setRejectModal({ show: true, eventId: event.event_id })} color="red">Reject</ActionBtn>
                      </div>
                    </td>
                  </Tr>
                ))}
              </tbody>
            </table>
          </div>
        </TableCard>
      </div>

      {/* Mobile Cards */}
      <div className="mobile-cards">
        <p className="text-xs font-bold mb-3" style={{ color: T.gold }}>{filteredEvents.length} event(s) pending approval</p>
        {filteredEvents.map((event, i) => (
          <div key={event.event_id || i} className="vrv-mobile-card">
            <p className="font-bold text-sm truncate mb-1" style={{ color: T.navy }}>{event.title || '—'}</p>
            <div className="grid grid-cols-2 gap-1 text-xs mb-3">
              <div><span style={{ color: T.muted }}>By: </span>{event.createdBy ? <button onClick={() => { const u = getUserObj(event.createdBy); if (u) setViewUser(u); }} className="font-bold" style={{ color: T.gold }}>{getUserName(event.createdBy) || '...'}</button> : '—'}</div>
              <div><span style={{ color: T.muted }}>Start: </span>{event.event_start_date ? new Date(event.event_start_date).toLocaleDateString() : '—'}</div>
              <div><span style={{ color: T.muted }}>End: </span>{event.event_end_date ? new Date(event.event_end_date).toLocaleDateString() : '—'}</div>
            </div>
            <div className="flex gap-2 pt-2" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
              <ActionBtn onClick={() => setViewEvent(event)} color="muted" small>View</ActionBtn>
              <ActionBtn onClick={() => handleAction(event.event_id, 'APPROVE')} color="green" small>Approve</ActionBtn>
              <ActionBtn onClick={() => setRejectModal({ show: true, eventId: event.event_id })} color="red" small>Reject</ActionBtn>
            </div>
          </div>
        ))}
      </div>

      {viewEvent && (
        <Modal onClose={() => setViewEvent(null)} title={viewEvent.title} subtitle="Event Preview" wide>
          <div className="grid grid-cols-2 gap-3 mb-4">
            {[['Start Date', viewEvent.event_start_date ? new Date(viewEvent.event_start_date).toLocaleString() : '—'], ['End Date', viewEvent.event_end_date ? new Date(viewEvent.event_end_date).toLocaleString() : '—']].map(([label, val]) => (
              <div key={label}>
                <p className="text-[10px] font-black uppercase tracking-widest mb-1" style={{ color: T.muted }}>{label}</p>
                <p className="text-sm font-medium px-3 py-2 rounded-lg" style={{ backgroundColor: T.stripe, color: T.ink, border: `1px solid ${T.border}` }}>{val}</p>
              </div>
            ))}
          </div>
          {viewEvent.event_image_url && <img src={viewEvent.event_image_url} alt={viewEvent.title} className="w-full max-h-[300px] object-contain rounded-xl mb-4" style={{ border: `1px solid ${T.border}` }} />}
          {viewEvent.event_details && <p className="text-sm leading-relaxed whitespace-pre-wrap mb-6" style={{ color: T.muted, borderTop: `1px solid ${T.border}`, paddingTop: '1rem' }}>{viewEvent.event_details}</p>}
          <PrimaryBtn onClick={() => setViewEvent(null)}>Close</PrimaryBtn>
        </Modal>
      )}
      {viewUser && <Modal onClose={() => setViewUser(null)} title="User Details" subtitle="Member Info" wide><UserDetailFields user={viewUser} /><div className="mt-6"><PrimaryBtn onClick={() => setViewUser(null)}>Close</PrimaryBtn></div></Modal>}
      {rejectModal.show && <RejectModal value={rejectComment} onChange={e => setRejectComment(e.target.value)} title="Reject Event" onCancel={() => { setRejectModal({ show: false, eventId: null }); setRejectComment(''); }} onConfirm={() => { handleAction(rejectModal.eventId, 'REJECT', rejectComment); setRejectModal({ show: false, eventId: null }); setRejectComment(''); }} />}
      <Toast toast={toast} onClose={() => setToast({ show: false, message: '', type: '' })} />
    </div>
  );
};

export default ApproveEventsSection;
